import React, { useEffect, useState } from 'react'
import axios from '../../../utils/axios'
import { InputField } from '../../molecules/input/input-field'
import { ReactComponent as SearchIcon } from '../../../assets/svgs/search-icon.svg'
import CustomTable, { TableColumn } from '../../molecules/table/table'
import NoData from '../../molecules/no-data/no-data'
import { Button } from '../../atoms/button/button'
import { useAuth } from '../../../customHooks/useAuth'
import { LIMIT_OPTIONS } from '../../../constants/constants'
import { t } from '../../../utils/i18n'
import './business-account.scss'
import { BUSINESS_ACCOUNTS } from '../../../constants/routes'
import { InfoTooltip } from '../../atoms/info-tooltip/info-tooltip'
import { mapBusinessAccountsData } from '../../../containers/utils'
import { DropdownOption } from '../../molecules/dropdown/dropdown'
import { BusinessManagerStatus, CanBeAnything } from '../../../types'

const RECORDS_PER_PAGE = process.env.REACT_APP_PAGINATION_LIMIT as string

export interface IBusinessAccount {
  isSetModalOpen: (val: boolean) => void
  isBMOnboarded?: boolean
  bmStatus?: DropdownOption
}
export const BusinessAccount = ({ isSetModalOpen, isBMOnboarded, bmStatus }: IBusinessAccount) => {
  const { user } = useAuth()
  const [businessAccounts, setBusinessAccounts] = useState<CanBeAnything[]>([])
  const [totalBusAcc, setTotalBusAcc] = useState(0)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(Number(RECORDS_PER_PAGE))
  const [isPageLoading, setIsPageLoading] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const getBusinessAccounts = async (withQuery: boolean, searchTerm?: string, status?: string) => {
    let url = ''
    if (status) {
      url = `${BUSINESS_ACCOUNTS}/?recordsPerPage=${limit}&page=${page}&searchFields=status&searchText=${status}`
    } else {
      url = withQuery
        ? `${BUSINESS_ACCOUNTS}/?recordsPerPage=${limit}&page=${page}`
        : searchTerm
          ? `${BUSINESS_ACCOUNTS}/?recordsPerPage=${limit}&page=${page}&searchFields=business_name,business_id&searchText=${searchTerm}`
          : `${BUSINESS_ACCOUNTS}?recordsPerPage=${limit}&page=${page}`
    }
    try {
      const config = {
        url,
        method: 'GET',
        headers: {
          'x-user': user?.email,
          'x-access-token': user?.tokens?.accessToken?.token,
          'x-source': user?.isSSO ? 'sso' : null,
        },
      }

      const businessAccRes = await axios(config)
      const mappedData = mapBusinessAccountsData(
        businessAccRes?.data?.data?.businessAccountsList as CanBeAnything[]
      )
      setBusinessAccounts(mappedData)
      setTotalBusAcc(businessAccRes?.data?.data?.pagination?.totalRecords)
      setIsLoading(false)
    } catch {
      setBusinessAccounts([])
    } finally {
      setIsLoading(false)
      setIsPageLoading(false)
    }
  }
  const handleSearch = (term: string) => {
    getBusinessAccounts(false, term)
    setSearchTerm(term)
  }
  useEffect(() => {
    if (bmStatus?.id) {
      if (bmStatus?.id.toLowerCase() === BusinessManagerStatus.all.toLowerCase()) {
        getBusinessAccounts(true)
      } else {
        getBusinessAccounts(true, '', bmStatus.id.toUpperCase())
      }
    }
    setIsLoading(true)
  }, [page, limit, isBMOnboarded, bmStatus])

  const syncKeys: TableColumn[] = [
    { key: 'businessUsersSync', label: t('businessUsersSync'), align: 'center' },
    { key: 'ownedAccountSync', label: t('ownedAccountSync'), align: 'center' },
    { key: 'clientAccountSync', label: t('clientAccountSync'), align: 'center' },
    { key: 'accountsUsersSync', label: t('accountsUsersSync'), align: 'center' },
    { key: 'ownedPageSync', label: t('ownedPageSync'), align: 'center' },
    { key: 'clientPageSync', label: t('clientPageSync'), align: 'center' },
    { key: 'pageUsersSync', label: t('pageUsersSync'), align: 'center' },
    { key: 'instaAccountSync', label: t('instaAccountSync'), align: 'center' },
    { key: 'ownedCatalogSync', label: t('ownedCatalogSync'), align: 'center' },
    { key: 'clientCatalogSync', label: t('clientCatalogSync'), align: 'center' },
    { key: 'catalogUsersSync', label: t('catalogUsersSync'), align: 'center' },
    { key: 'ownedPixleSync', label: t('ownedPixleSync'), align: 'center' },
    { key: 'clientPixleSync', label: t('clientPixleSync'), align: 'center' },
    { key: 'datasetUsersSync', label: t('datasetUsersSync'), align: 'center' },
  ]

  const columns: TableColumn[] = [
    { key: 'businessId', label: t('businessId'), width: 180, fixed: true, align: 'left' },
    { key: 'businessName', label: t('businessName'), width: 270, fixed: true, align: 'left' },
    { key: 'status', label: t('status'), width: 100, fixed: true, align: 'left' },
    { key: '', label: t('diagnosticUsage'), width: 100, fixed: true, align: 'left' },
    ...syncKeys.map(({ key, label, align }) => ({
      key,
      label,
      width: 140,
      fixed: false,
      align,
    })),
  ]

  return (
    <section className="business-acc-section">
      <div className="top-sec">
        <div className="business-tooltip-container">
          <h1>{t('business-accounts.heading')}</h1>
          <InfoTooltip
            styleClass="input-info"
            tooltipContent={t('business-accounts.tooltip')}
            placement={'right'}
          />
        </div>
        {!isPageLoading && businessAccounts.length >= 0 && (
          <InputField
            fieldType="text"
            label=""
            value={searchTerm}
            onChange={(s) => handleSearch(s)}
            placeholder={t('business-accounts.search.placeholder')}
            icon={<SearchIcon />}
          />
        )}
      </div>
      <div className="bottom-sec">
        {isPageLoading ? (
          <div className="link-acc-pulsating" />
        ) : businessAccounts.length >= 1 || !isLoading ? (
          <CustomTable
            data={businessAccounts}
            columns={columns}
            isPaginated={true}
            hasLegends={true}
            isLoading={isLoading}
            recordsPerPage={limit}
            pageRange={LIMIT_OPTIONS}
            currentPage={page}
            onPageChange={setPage}
            onLimitChange={(newLimit: number | null) => {
              setLimit(Number(newLimit))
              setPage(1)
            }}
            total={totalBusAcc}
          />
        ) : (
          <NoData
            message={t('no.business.acc')}
            child={
              <Button
                label={t('add.business.account')}
                handleClick={() => isSetModalOpen(true)}
                removeDefaultCss
              />
            }
          />
        )}
      </div>
    </section>
  )
}

export default BusinessAccount
